import { useAccounts, type AccountInfo } from '@features/trades/hooks/useTradeMetadata';
import { useAccount } from '@shared/state/accountStore';
import { useCqgDisplayState } from '@features/cqg/hooks/useCqgDisplayState';
import { useRithmicDisplayState } from '@features/rithmic/hooks/useRithmicDisplayState';
import { readStoredCqgCreds } from '@features/cqg/lib/cqgCreds';
import { readAccountLogin } from '../lib/accountLogins';
import {
  accountBelongsToActiveConnect,
  deriveAccountConnection,
  deriveAggregateAlert,
  deriveSelectedAccountState,
  type AccountConnectionInfo,
  type ProviderAlert,
  type ProviderSnapshot,
  type SelectedAccountState,
} from '../lib/accountConnection';

/**
 * The account↔connection join for the Accounts panel + topbar control: combines `useAccounts()` with
 * both providers' display state (see `accountConnection.ts` for the pure rules and their tests).
 * All inputs are shared query caches / the relay singletons, so this is cheap to mount anywhere.
 */
export function useAccountConnection(): {
  /** The `useAccounts()` query (already filtered of 'manual'). */
  accounts: ReturnType<typeof useAccounts>;
  /** Selected account name from the global store (the scoped-query key). */
  selectedName: string;
  /** Per-row derivation for panel rows. */
  forAccount: (a: AccountInfo) => AccountConnectionInfo;
  /** The SELECTED account's control state (chip + mismatch + label). */
  selected: SelectedAccountState;
  /** Cross-provider error/concurrent indicator (aggregate dot) — null when healthy. */
  alert: ProviderAlert | null;
  cqg: ProviderSnapshot;
  rithmic: ProviderSnapshot;
} {
  const accounts = useAccounts();
  const selectedName = useAccount();
  const cqgState = useCqgDisplayState();
  const rithmicState = useRithmicDisplayState();

  const cqg: ProviderSnapshot = { display: cqgState.display, accounts: cqgState.accounts, reason: cqgState.reason };
  const rithmic: ProviderSnapshot = { display: rithmicState.display, accounts: rithmicState.accounts, reason: rithmicState.reason };

  // The identity of the CQG login currently being connected — used to scope the "connecting" state to
  // the accounts that actually belong to it (a demo connect must not light up a live login's rows).
  // Only consulted while a session is mid-handshake; stored creds hold that connect's env + username.
  const activeCreds = readStoredCqgCreds();
  const active = { environment: activeCreds.environment, username: activeCreds.username || null };
  const belongs = (a: Pick<AccountInfo, 'name' | 'provider' | 'environment'>) =>
    accountBelongsToActiveConnect(a, active, readAccountLogin(a.name)?.username ?? null);

  const selectedInfo = accounts.data?.find(a => a.name === selectedName);

  return {
    accounts,
    selectedName,
    forAccount: (a: AccountInfo) => deriveAccountConnection(a, cqg, rithmic, belongs(a)),
    selected: deriveSelectedAccountState(
      selectedName, accounts.data, accounts.isPending, cqg, rithmic,
      selectedInfo ? belongs(selectedInfo) : true,
    ),
    alert: deriveAggregateAlert(cqg, rithmic),
    cqg,
    rithmic,
  };
}
